import {useMutation} from "@apollo/client";
import classNames from "classnames";
import {motion} from "motion/react";
import {useEffect, useMemo, useState} from "react";
import {FiChevronRight, FiPlus} from "react-icons/fi";
import {LuChartBar} from "react-icons/lu";

import Button from "@/components/button/Button.tsx";
import {Chip} from "@/components/chip/Chip.tsx";
import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {Icon} from "@/components/icon/Icon.tsx";
import {useFilteredRowSelection} from "@/components/table/hooks/useFilteredRowSelection.ts";
import {EmptyTable} from "@/components/table/subComponents/EmptyTable/EmptyTable.tsx";
import {TableRowDragHandleCell} from "@/components/table/subComponents/TableRowDragHandleCell/TableRowDragHandleCell.tsx";
import {Table} from "@/components/table/Table.tsx";
import {ColumnSpec} from "@/components/table/Table.types.ts";
import {Tooltip} from "@/components/toolTip/Tooltip.tsx";
import {
  GQL_STATUS_PAGE_QUERY,
  GQL_STATUSPAGE_COMPONENTS,
  GQL_UPDATE_COMPONENTS_ORDER,
} from "@/lib/queries/statuspages.ts";
import {Component, ComponentStatus} from "@/lib/types/statuspages.ts";
import ComponentStatusCell from "@/pages/statuspages/tabs/components/componentsTable/subComponents/ComponentStatusCell.tsx";

import {createHandleDragEnd} from "./StatusPageComponentsTable.utils.ts";
import {StatusPageComponentsRowActions} from "./subComponents/StatusPageComponentsRowActions.tsx";
import {TableBulkActionsBar} from "./subComponents/TableBulkActionsBar.tsx";

import styles from "./StatusPageComponentsTable.module.scss";

interface StatusPageComponentsTableV2Props {
  data: {
    data: Component[];
    total: number;
  };
  dataTestId?: string;
  statusPageId: number;
  componentStatuses: ComponentStatus[];
  isFiltered?: boolean;
  isLoading?: boolean;
}

export const StatusPageComponentsTableV2 = ({
  data,
  dataTestId,
  statusPageId,
  componentStatuses,
  isFiltered,
  isLoading,
}: StatusPageComponentsTableV2Props) => {
  const {user} = useCurrentUserContext();
  const {rowSelection, setRowSelection} = useFilteredRowSelection({
    data: data.data,
    getRowId: (row) => row.id.toString(),
    getSubRows: (row) => row.subcomponents,
  });
  const [components, setComponents] = useState<Component[]>(data.data);
  const [updateOrder] = useMutation(GQL_UPDATE_COMPONENTS_ORDER, {
    refetchQueries: [GQL_STATUS_PAGE_QUERY, GQL_STATUSPAGE_COMPONENTS],
    awaitRefetchQueries: true,
  });

  useEffect(() => {
    setComponents(data.data);
  }, [data.data]);

  const handleDragEnd = useMemo(
    () =>
      createHandleDragEnd({
        components,
        statusPageId,
        updateOrder,
        setComponents,
      }),
    [components, statusPageId, updateOrder]
  );

  const specs: ColumnSpec<Component>[] = [
    {
      fieldName: "name",
      fieldTitle: "Name",
      meta: {
        headerClassName: styles.nameCell,
        columnClassName: styles.nameColumn,
      },
      display: (cellContext) => {
        if (!cellContext) {
          return null;
        }
        const row = cellContext.row;
        const component = row.original;
        return (
          <div
            className={classNames("d-flex align-items-center gap-2", {
              "ps-5": row.depth > 0,
              "ps-2": row.depth === 0 && component.subcomponents.length === 0,
            })}
          >
            {component.is_group && component.subcomponents.length > 0 && (
              <Button onClick={row.getToggleExpandedHandler()} size={"fitContent"} variant={"ghost"}>
                <motion.div
                  initial={false}
                  animate={{rotate: row.getIsExpanded() ? 90 : 0}}
                  transition={{duration: 0.2, ease: "easeInOut"}}
                >
                  <Icon icon={FiChevronRight} />
                </motion.div>
              </Button>
            )}
            <Tooltip tooltipContent={component.name} delayMs={500}>
              <div className={classNames("ellipsis-overflow", {pulseTextBodySmallBold: component.is_group})}>
                {component.name}
              </div>
            </Tooltip>
            {component.is_group && (
              <Chip variant={"metric"} size={"tinyMedium"} className={"pulse-color-gray-600 text-nowrap"}>
                {component.subcomponents.length} {component.subcomponents.length === 1 ? "Component" : "Components"}
              </Chip>
            )}
            {!component.is_group && component.service && (
              <Tooltip tooltipContent={"Response time metrics will be displayed for this Component."}>
                <Icon icon={LuChartBar} className={"pulse-color-gray-600"} />
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      fieldName: "status",
      fieldTitle: "Status",
      meta: {
        headerClassName: styles.statusCell,
      },
      accessor: (component: Component) => (
        <>
          {(!component.is_group || (component.is_group && component.subcomponents.length > 0)) && (
            <ComponentStatusCell
              component={component}
              componentStatuses={componentStatuses}
              statusPageID={statusPageId}
              variant={user?.has_write_access ? "select" : "icon"}
            />
          )}
        </>
      ),
    },
  ];

  if (user?.has_write_access) {
    specs.push({
      fieldName: "actions",
      size: 40,
      meta: {
        headerClassName: styles.actionCell,
        columnClassName: styles.actionCell,
      },
      accessor: (component: Component) => (
        <StatusPageComponentsRowActions
          key={component.id}
          statusPageID={statusPageId}
          component={component}
          components={components}
          componentStatuses={componentStatuses}
        />
      ),
    });
    specs.push({
      fieldName: "id",
      size: 40,
      meta: {
        headerClassName: styles.actionCell,
        columnClassName: styles.actionCell,
      },
      display: (cellContext) => {
        if (!cellContext) {
          return null;
        }
        const row = cellContext.row;
        return (
          <div className={"d-flex align-items-center"}>
            <Tooltip
              tooltipContent={
                isFiltered
                  ? "Clear search to enable reordering"
                  : "Arrange how components will appear on your status page."
              }
            >
              <div>
                <TableRowDragHandleCell rowId={row.id} disabled={isFiltered} />
              </div>
            </Tooltip>
          </div>
        );
      },
    });
  }

  return (
    <>
      <Table
        isDragAndDrop={user?.has_write_access && !isFiltered}
        onDragEnd={handleDragEnd}
        rows={components}
        specs={specs}
        initialState={{columnPinning: {right: ["actions", "id"]}}}
        fixedWidth={true}
        minWidth={"640px"}
        rowSelection={rowSelection}
        setRowSelection={setRowSelection}
        expandableSubRows={true}
        subRowEndSpacer={true}
        getSubRows={(row) => (row.is_group && row.subcomponents ? row.subcomponents : [])}
        tableRowClassName={styles.tableRow}
        actionBar={
          <TableBulkActionsBar
            selectedItems={rowSelection}
            setSelectedItems={setRowSelection}
            statusPageId={statusPageId}
          />
        }
        getRowId={(row) => row.id.toString()}
        isLoading={isLoading}
        noDataComponent={
          <EmptyTable colSpan={specs.length + (rowSelection ? 1 : 0)}>
            {isFiltered ? (
              <div className={"d-flex"}>No Components found matching your search above.</div>
            ) : (
              <div className={"d-flex flex-column align-items-center gap-4 py-5"}>
                <div>
                  <Icon icon={FiPlus} className={"pulse-color-gray-500"} />
                </div>
                <div className={"d-flex flex-column align-items-center gap-1"}>
                  <div className={"pulseTextH2Bold"}>Add components to this status page</div>
                  <div className={"pulse-color-gray-700"}>
                    Add components or component groups to your status page (sites, APIs, databases etc).
                  </div>
                  <div className={"pulse-color-gray-700"}>
                    Optionally link to Uptime Checks to show response time stats.
                  </div>
                </div>
              </div>
            )}
          </EmptyTable>
        }
        dataTestId={dataTestId}
      />
    </>
  );
};
